import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { formatPrice } from "../lib/utils";
import {
  CreditCard,
  Minus,
  Plus,
  ShoppingCart,
  Tag,
  Trash2,
  X
} from "lucide-react";

const AVAILABLE_DISCOUNTS = {
  RG10: { type: "percent", value: 10, label: "RG10 (10%)" },
  RG20: { type: "percent", value: 20, label: "RG20 (20%)" },
  BIENVENIDA: { type: "fixed", value: 5000, label: "BIENVENIDA ($5000)" },
};

const Cart = ({ variant = "overlay" }) => {
  const { selectedPlan, items, updateQuantity, removeItem, clearCart, isCartOpen, closeCart } = useCart();
  const navigate = useNavigate();
  const cartRef = useRef(null);
  const isOverlay = variant === "overlay";
  const isPage = variant === "page";
  const isOpen = isOverlay ? isCartOpen : true;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cartRef.current && !cartRef.current.contains(event.target)) {
        closeCart();
      }
    };

    if (isOverlay && isCartOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCartOpen, closeCart, isOverlay]);

  const cartTotal = useMemo(
    () => items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [items]
  );
  const [discountCode, setDiscountCode] = React.useState("");
  const [appliedDiscount, setAppliedDiscount] = React.useState(null);
  const [discountError, setDiscountError] = React.useState("");

  const discountAmount = useMemo(() => {
    if (!isPage || !appliedDiscount) return 0;
    if (appliedDiscount.type === "percent") {
      return Math.round(cartTotal * (appliedDiscount.value / 100));
    }
    if (appliedDiscount.type === "fixed") {
      return Math.min(appliedDiscount.value, cartTotal);
    }
    return 0;
  }, [appliedDiscount, cartTotal, isPage]);

  const finalTotal = Math.max(cartTotal - discountAmount, 0);
  const hasItems = items.length > 0;

  if (!isOpen) {
    return null;
  }

  const handleProceedToCheckout = () => {
    if (isOverlay) {
      closeCart();
    }
    navigate("/checkout");
  };

  const handleApplyDiscount = () => {
    if (!isPage) return;
    const normalized = discountCode.trim().toUpperCase();

    if (!normalized) {
      setAppliedDiscount(null);
      setDiscountError("Ingresa un codigo para aplicar.");
      return;
    }

    const discount = AVAILABLE_DISCOUNTS[normalized];
    if (!discount) {
      setAppliedDiscount(null);
      setDiscountError("El codigo ingresado no es valido.");
      return;
    }

    setAppliedDiscount({ ...discount, code: normalized });
    setDiscountError("");
  };

  const handleClearCart = () => {
    clearCart();
    setAppliedDiscount(null);
    setDiscountCode("");
    setDiscountError("");
  };

  const containerClasses = isOverlay
    ? "fixed inset-0 z-[60] flex items-end justify-center bg-black/30 p-4 md:items-start md:justify-end md:p-6"
    : "min-h-screen bg-transparent px-4 py-10";

  const cardHeightClasses = isOverlay
    ? "h-[calc(100vh-2rem)] md:h-[calc(100vh-6rem)]"
    : "min-h-[calc(100vh-5rem)]";

  return (
    <div className={containerClasses}>
      <div ref={cartRef} className={`w-full ${isOverlay ? "max-w-md md:mt-16" : "max-w-2xl mx-auto"}`}>
        <Card className={`cart-scope w-full ${cardHeightClasses} plan-card text-card-foreground flex flex-col`}>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <ShoppingCart className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg font-semibold tracking-tight">
                Carrito de compras
              </CardTitle>
            </div>
            {isOverlay && (
              <Button variant="ghost" size="icon" onClick={closeCart} className="rounded-full">
                <X className="h-5 w-5" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="flex-1 flex flex-col overflow-hidden px-6 pb-6">
            <div className="flex-1 overflow-y-auto pr-2 -mr-2 space-y-6 no-scrollbar">
              {selectedPlan && (
                <div className="plan-card p-3">
                  <p className="text-sm font-semibold text-foreground">{selectedPlan.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">{selectedPlan.description}</p>
                </div>
              )}

              <div>
              <div className="mb-2">
                <span className="text-sm font-medium">Resumen de productos</span>
              </div>
                {items.length ? (
                  <div className="space-y-3">
                    {items.map((item) => (
                      <div
                        key={`${item.id}-${item.variantKey}`}
                        className="plan-card p-3 space-y-2"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-border/60 bg-muted/40">
                              {item.image ? (
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  className="h-full w-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="h-full w-full bg-muted/60" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground">{item.name}</p>
                              <p className="text-xs text-muted-foreground">{item.category}</p>
                              {item.variantLabel && (
                                <p className="text-xs text-muted-foreground mt-1">{item.variantLabel}</p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(item.id, item.variantKey)}
                            className="rounded-full"
                            aria-label={`Quitar ${item.name}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center rounded-lg border border-[#ff3b1f]/40 px-2 py-1">
                          {item.quantity <= 1 ? (
                            <button
                              type="button"
                              onClick={() => removeItem(item.id, item.variantKey)}
                              className="h-8 w-8 rounded-md text-[#ff3b1f] font-semibold"
                              aria-label={`Quitar ${item.name}`}
                            >
                              <Trash2 className="h-4 w-4 mx-auto" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.id, item.variantKey, -1)}
                              className="h-8 w-8 rounded-md text-[#ff3b1f] font-semibold"
                              aria-label={`Reducir cantidad de ${item.name}`}
                            >
                              <Minus className="h-4 w-4 mx-auto" />
                            </button>
                          )}
                          <span className="text-sm font-semibold text-foreground w-6 text-center">
                            {item.quantity}
                          </span>
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.id, item.variantKey, 1)}
                              className="h-8 w-8 rounded-md text-[#ff3b1f] font-semibold disabled:opacity-40"
                              disabled={item.stock !== undefined && item.quantity >= item.stock}
                              aria-label={`Aumentar cantidad de ${item.name}`}
                            >
                              <Plus className="h-4 w-4 mx-auto" />
                            </button>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Subtotal</p>
                            <p className="text-sm font-semibold text-foreground">
                              {formatPrice(item.price * item.quantity)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Tu carrito esta vacio.</p>
                )}
              </div>

              <Separator />

              {isPage && (
                <>
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Metodo de pago</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Coordinamos el pago al finalizar por WhatsApp.
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Codigo de descuento</span>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Ingresar codigo"
                        className="flex-1"
                        value={discountCode}
                        onChange={(event) => setDiscountCode(event.target.value)}
                      />
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={handleApplyDiscount}
                        className="bg-muted text-foreground hover:bg-muted/80"
                      >
                        Aplicar
                      </Button>
                    </div>
                    {appliedDiscount && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Codigo aplicado: {appliedDiscount.label}
                      </p>
                    )}
                    {discountError && (
                      <p className="mt-2 text-xs text-red-500">
                        {discountError}
                      </p>
                    )}
                  </div>

                  <Separator />
                </>
              )}

              {hasItems && (
                <div>
                  <span className="text-sm font-medium">Total del pedido</span>
                  <div className="grid grid-cols-2 gap-y-2 text-sm mt-2">
                    <span className="text-muted-foreground">Total productos:</span>
                    <span className="text-right font-medium">{formatPrice(cartTotal)}</span>
                    {isPage && (
                      <>
                        <span className="text-muted-foreground">Descuento:</span>
                        <span className="text-right font-medium">
                          {discountAmount > 0 ? `-${formatPrice(discountAmount)}` : formatPrice(0)}
                        </span>
                      </>
                    )}
                    <span className="text-muted-foreground">Envio:</span>
                    <span className="text-right font-medium">Gratis</span>
                    <span className="text-muted-foreground">Total final:</span>
                    <span className="text-right font-semibold">{formatPrice(finalTotal)}</span>
                  </div>
                </div>
              )}
            </div>

            {(selectedPlan || items.length > 0) && (
              <div className="pt-4 space-y-3">
                <Button
                  className="cart-primary w-full bg-foreground text-background hover:bg-foreground/90"
                  onClick={handleProceedToCheckout}
                >
                  Proceder al pago
                </Button>
                <Button
                  variant="secondary"
                  className="w-full bg-muted text-foreground hover:bg-muted/80"
                  onClick={closeCart}
                >
                  Seguir comprando
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClearCart}
                  size="lg"
                  className="w-full"
                >
                  Vaciar carrito
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Cart;
